import React, { useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { trackException } from '../services/appInsightsService';

const BASE_URL = () =>
    (process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api');

/**
 * TierDistribution — Dashboard widget showing tenant counts by plan tier.
 * Calls GET /platform/tier-distribution.
 */
const TierDistribution = () => {
    const [distribution, setDistribution] = useState(null);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchDistribution = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await fetchWithAuth(`${BASE_URL()}/platform/tier-distribution`, { method: 'GET' });
            if (response.ok) {
                const data = await response.json();
                setDistribution(data.distribution || {});
                setTotal(data.totalTenants || 0);
            } else {
                setError(`Failed to load tier distribution: ${response.status}`);
            }
        } catch (err) {
            trackException(err, { operation: 'TierDistribution.fetchDistribution' });
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDistribution();
    }, [fetchDistribution]);

    // Unified Plan Catalog vocabulary (~/.claude/plans/golden-napping-planet.md §0-1): Free,
    // Meeting, Meeting Pro, Webinar, Unlimited. Retired-tier colors are kept alongside the new
    // ones — versioned activation means old and new tenant tier labels can briefly coexist
    // during the P1 backend migration (see PlanContext.jsx TIER_FEATURES comment on ZoomClient
    // for the same rationale) — and any name this map doesn't recognize falls back to Unknown.
    const TIER_COLORS = {
        Free: '#6b7280',
        Meeting: '#3b82f6',
        'Meeting Pro': '#f59e0b',
        Webinar: '#8b5cf6',
        Starter: '#3b82f6',
        Standard: '#8b5cf6',
        Professional: '#f59e0b',
        Advanced: '#10b981',
        Business: '#ef4444',
        Enterprise: '#dc2626',
        Unlimited: '#1d4ed8',
        Unknown: '#d1d5db'
    };

    if (isLoading) return <div style={{ padding: 16 }}>Loading tier distribution...</div>;
    // Plain text, no notice background -- matches this component's other states (loading above,
    // "No tenant data available" below), none of which use a tinted background. --text-primary
    // is theme-aware (unlike the static 'red'), so the message stays readable in both themes.
    if (error) return <div style={{ padding: 16, color: 'var(--text-primary, #111827)' }}>{error} <button onClick={fetchDistribution}>Retry</button></div>;
    if (!distribution) return null;

    const entries = Object.entries(distribution).sort((a, b) => b[1] - a[1]);

    return (
        <div style={{ padding: 16, border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 8, maxWidth: 400 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>Tier Distribution</h3>
                <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>{total} total tenants</span>
            </div>

            {entries.length === 0 ? (
                <div style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>No tenant data available.</div>
            ) : (
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: 12, color: 'var(--text-secondary, #6b7280)', fontWeight: 600 }}>Tier</th>
                            <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: 12, color: 'var(--text-secondary, #6b7280)', fontWeight: 600 }}>Count</th>
                            <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: 12, color: 'var(--text-secondary, #6b7280)', fontWeight: 600 }}>%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map(([tier, count]) => {
                            const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
                            const color = TIER_COLORS[tier] || TIER_COLORS.Unknown;
                            return (
                                <tr key={tier}>
                                    <td style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
                                        <span style={{ fontSize: 13 }}>{tier}</span>
                                    </td>
                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>{count}</td>
                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>{pct}%</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            <button
                onClick={fetchDistribution}
                style={{
                    marginTop: 12,
                    fontSize: 12,
                    padding: '4px 10px',
                    background: 'none',
                    border: '1px solid var(--border-color, #d1d5db)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    color: 'var(--text-primary, #111827)',
                }}
            >
                Refresh
            </button>
        </div>
    );
};

export default TierDistribution;
